import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { systemApi } from '@/api/system';
import { useAuth } from '@/auth/AuthProvider';
import { readRequiredString } from '@/shared/wire';
import {
  SYSTEM_DATE_REFRESH_EVENT,
  useSystemDateStore,
} from './systemDateStore';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function normalizeBusinessDatePayload(payload: unknown): {
  businessDate: string;
  systemTimestamp: string | null;
} {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Invalid business date payload');
  }

  const record = payload as Record<string, unknown>;
  const businessDate = readRequiredString(record, 'business_date', 'businessDate');
  if (!businessDate) {
    throw new Error('Missing business date');
  }

  return {
    businessDate,
    systemTimestamp: readRequiredString(record, 'system_timestamp', 'systemTimestamp') ?? null,
  };
}

type SystemDateBootstrapProps = {
  children: React.ReactNode;
};

export function SystemDateBootstrap({ children }: SystemDateBootstrapProps) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const businessDate = useSystemDateStore((s) => s.businessDate);
  const status = useSystemDateStore((s) => s.status);
  const errorMessage = useSystemDateStore((s) => s.errorMessage);
  const setLoading = useSystemDateStore((s) => s.setLoading);
  const setSystemDate = useSystemDateStore((s) => s.setSystemDate);
  const setError = useSystemDateStore((s) => s.setError);
  const reset = useSystemDateStore((s) => s.reset);

  useEffect(() => {
    if (!user) {
      reset();
      return;
    }

    let cancelled = false;

    const refresh = async (): Promise<void> => {
      if (!businessDate) setLoading();
      try {
        const payload = await systemApi.businessDate();
        if (cancelled) return;
        setSystemDate(normalizeBusinessDatePayload(payload));
      } catch {
        if (cancelled) return;
        setError(t('systemDate.loadError'));
      }
    };

    void refresh();

    const onFocus = () => {
      void refresh();
    };
    const onManualRefresh = () => {
      void refresh();
    };

    const timer = window.setInterval(() => {
      void refresh();
    }, REFRESH_INTERVAL_MS);

    window.addEventListener('focus', onFocus);
    window.addEventListener(SYSTEM_DATE_REFRESH_EVENT, onManualRefresh);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(SYSTEM_DATE_REFRESH_EVENT, onManualRefresh);
    };
  }, [businessDate, reset, setError, setLoading, setSystemDate, t, user]);

  if (!businessDate && (status === 'idle' || status === 'loading')) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-foreground">
        <p className="text-sm text-muted-foreground">{t('systemDate.loading')}</p>
      </div>
    );
  }

  if (!businessDate && status === 'error') {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-foreground">
        <div className="flex max-w-sm flex-col items-center gap-3 rounded-lg border border-border bg-card p-6 text-center">
          <p className="text-sm font-medium">{t('systemDate.errorTitle')}</p>
          <p className="text-xs text-muted-foreground">{errorMessage ?? t('systemDate.loadError')}</p>
          <button
            type="button"
            onClick={() => {
              reset();
              window.dispatchEvent(new Event(SYSTEM_DATE_REFRESH_EVENT));
            }}
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('systemDate.retry')}
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
